import { Component } from '@angular/core';
import {
  CompanyResponse,
  UserEntityRelationWithoutUserDataResponse,
  UserResponse
} from '../../@shared/models/response.module';
import {StorageService} from '../../@core/services/storage.service';
import {CompanyService} from '../../@shared/services/company.service';
import {EndpointService} from '../../@shared/endpoints.service';
import {COR_COY_0} from '../../@shared/models/input.module';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EditCompanyImages} from './components/company-profile-overview/components/edit-company-images/edit-company-images';
import {UpdateCompanyImage} from './components/update-company-image/update-company-image';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastService} from '../../@core/services/toast.service';
import {forkJoin} from 'rxjs';

@Component({
  selector: 'app-company-profile',
  standalone: false,
  templateUrl: './company-profile.html',
  styleUrl: './company-profile.css'
})
export class CompanyProfile {
  user: UserResponse
  selectedCompany: UserEntityRelationWithoutUserDataResponse
  company: CompanyResponse
  selectedTab: string = 'information'

  // Onboarding completion tracking
  hasNewsPosts: boolean = false;
  hasContacts: boolean = false;
  hasSponsors: boolean = false;
  hasDocuments: boolean = false;
  hasEvents: boolean = false;

  constructor(private storageService: StorageService,
              private companyService: CompanyService,
              private endpointService: EndpointService,
              private modalService: NgbModal,
              private route: ActivatedRoute,
              private router: Router,
              private toastService: ToastService) {
  }

  ngOnInit() {
    this.selectedCompany = this.storageService.getSelectedCompany()
    this.getCompany();

    // Read tab and handle other query params
    this.route.queryParams.subscribe(params => {
      if (params['tab']) {
        this.selectedTab = params['tab'];
      }
      // Handle subscription upgrade cancelled
      if (params['upgrade_cancelled'] === 'true') {
        this.toastService.info('Subscription upgrade was cancelled.');
        // Clean up URL but keep tab param
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: params['tab'] || this.selectedTab },
          replaceUrl: true
        });
      }
    });
  }

  getCompany() {
    let params: COR_COY_0 = {
      company_id: this.selectedCompany.entity_id
    }

    this.companyService.getCompanyById(params).subscribe((res) => {
      this.company = res;
      console.log("Company Data: ", this.company);
      // Load onboarding status after company data is loaded
      this.loadOnboardingStatus();
    })
  }

  loadOnboardingStatus() {
    const companyId = this.selectedCompany.entity_id;

    // Load news/feed posts count - using FEED-1 endpoint
    this.endpointService.feed_1({ company_id: companyId, limit: 1 }).subscribe({
      next: (res) => {
        this.hasNewsPosts = res.data && res.data.length > 0;
      },
      error: () => this.hasNewsPosts = false
    });

    // Check contacts - using company email/phone as indicator
    this.hasContacts = !!(this.company.email && this.company.phone);

    // Load sponsors count - using COR-COY-20 endpoint
    this.endpointService.cor_coy_20(companyId).subscribe({
      next: (res) => {
        this.hasSponsors = res.data && res.data.length > 0;
      },
      error: () => this.hasSponsors = false
    });

    // Load documents count - using COR-COY-27 endpoint
    this.endpointService.cor_coy_27({ company_id: companyId }).subscribe({
      next: (res) => {
        this.hasDocuments = res.data && res.data.length > 0;
      },
      error: () => this.hasDocuments = false
    });

    // Load events count - using AQH-EVE-0 endpoint
    this.endpointService.aqh_eve_0({ company_id: companyId, limit: 1 }).subscribe({
      next: (res) => {
        this.hasEvents = res.data && res.data.length > 0;
      },
      error: () => this.hasEvents = false
    });
  }

  onNavigateToTab(tab: string) {
    this.selectedTab = tab;
    // Scroll to top of the tab content
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Called when any child component updates data
  onDataChanged() {
    // Refresh company data
    this.getCompany();
  }

  // Switch tab and refresh when coming back
  selectTab(tab: string) {
    // If switching away from a tab, refresh status when we come back
    if (this.selectedTab !== tab) {
      this.selectedTab = tab;
      // Refresh onboarding status after a small delay to allow for any saves
      setTimeout(() => this.loadOnboardingStatus(), 500);
    }
  }

  openUpdateLogoModal() {
    let modalRef = this.modalService.open(EditCompanyImages, {centered: true, size: 'lg'});
    modalRef.componentInstance.company = this.company;
    modalRef.result.then((result) => {
      if (result) {
        this.getCompany();
      }
    }).catch(() => {});
  }

  openUpdateImageModal(imageType: 'header' | 'logo') {
    let modalRef = this.modalService.open(UpdateCompanyImage, {centered: true, size: 'md'});
    modalRef.componentInstance.company = this.company;
    modalRef.componentInstance.imageType = imageType;
    modalRef.result.then((result) => {
      if (result) {
        this.getCompany();
      }
    }).catch(() => {});
  }

}
